import { useState, FormEvent } from 'react'

import { useUser } from '@hooks/user'
import { Dialog, DialogProps } from '@components/UI/Dialog'
import { Label } from '@components/UI/Form/Label'
import { Input } from '@components/UI/Form/Input'
import { Button } from '@components/UI/Button'
import { Spinner } from '@components/UI/Spinner'
import { useRouter } from 'next/router'
import { XIcon } from '@heroicons/react/outline'
import ReactGA from 'react-ga4'
import { isProduction } from '@pages/_app'

interface LoginDialogProps extends Omit<DialogProps, 'width'> {
  isOpen: boolean
  redirectUrl?: string
  handleClose: () => void
}

export function LoginDialog(props: LoginDialogProps) {
  const { login } = useUser()
  const { push } = useRouter()
  const [error, setError] = useState('')

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()

    async function attemptLogin(email: string, password: string) {
      try {
        await login.mutateAsync({ email, password })

        if (props.redirectUrl) {
          void push(props.redirectUrl)
        } else {
          isProduction &&
            ReactGA.event({
              category: 'Checkout Actions',
              action: 'User Login'
              // label: email
            })
          props.handleClose()
        }
      } catch (error) {
        if (!error.response) {
          setError('There was an error')
        } else if (error.response.status === 404) {
          setError('Your email or password is incorrect')
        } else if (error.response.data) {
          setError(error.response.data.message)
        }
      }
    }

    const data = new FormData(e.currentTarget)
    const email = data.get('email') as string
    const password = data.get('password') as string

    void attemptLogin(email, password)
  }

  return (
    <Dialog
      isOpen={props.isOpen}
      handleClose={props.handleClose}
      width="lg"
      hideHeader={true}
    >
      <div className="relative p-8">
        <div
          className="absolute top-4 right-4 p-2 text-black-300 cursor-pointer"
          onClick={props.handleClose}
          data-testid="closeDialog"
        >
          <XIcon width={24} height={24} />
        </div>

        <img
          src="/images/thumbtack/wordmark_dark.png"
          className="w-48 mx-auto mb-8"
        />
        <h1 className="font-bold tracking-tight mb-8 text-xl text-center">
          Log In with Email
        </h1>
        <form onSubmit={handleSubmit}>
          <section className="mb-4">
            <Label htmlFor="login-dialog-email">Email</Label>
            <Input
              id="login-dialog-email"
              name="email"
              type="email"
              required
              placeholder="example@example.com"
            />
          </section>
          <section className="mb-8">
            <Label htmlFor="login-dialog-password">Password</Label>
            <Input
              id="login-dialog-password"
              name="password"
              type="password"
              required
            />
          </section>

          {error && <p className="mb-8 text-red-400 text-center">{error}</p>}

          <section className="flex justify-center">
            {login.status === 'loading' ? <Spinner /> : <Button>Log In</Button>}
          </section>

          <p className="text-xs text-center mt-8">
            Your Home Repair Estimate login credentials are separate from your
            Thumbtack credentials.
          </p>
        </form>
      </div>
    </Dialog>
  )
}
